import React, { useEffect, useRef, useState } from 'react';
import packageJson from '../package.json';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ContactUsPage from './pages/ContactUsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPlansPage from './pages/AdminPlansPage';
import AdminIncidentsPage from './pages/AdminIncidentsPage';
import AdminAuditLogsPage from './pages/AdminAuditLogsPage';
import AdminEncryptionPage from './pages/AdminEncryptionPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SubscriptionCheckPage from './pages/SubscriptionCheckPage';
import ProfilePage from './pages/ProfilePage';
import ConfirmActivePage from './pages/ConfirmActivePage';
import NomineeObjectionPage from './pages/NomineeObjectionPage';
import ReferralPage from './pages/ReferralPage';
import NomineesPage from './pages/NomineesPage';
import { fetchServerVersion } from './utils/publicApi';
import MaintenanceInfo from './components/MaintenanceInfo';
import AboutUsPage from './pages/AboutUsPage';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CancellationRefundsPage from './pages/CancellationRefundsPage';
import GrievanceRedressalPage from './pages/GrievanceRedressalPage';
import CookieConsentBanner from './components/CookieConsentBanner';
import { setAnalyticsConsent, trackDeviceDiagnostics, trackPageView } from './utils/analytics';
import {
  readCookiePreferences,
  saveCookiePreferences,
  subscribeToCookiePreferences,
} from './utils/cookieConsent';
import { pushAnalyticsConsent, reconcileAnalyticsConsent } from './utils/analyticsConsentSync';
import { logout } from './utils/authApi';
import { restoreSession } from './utils/sessionRestore';
import SessionTimeoutPage from './pages/SessionTimeoutPage';
import NomineeAccessInfoPage from './pages/NomineeAccessInfoPage';
import QuickUnlockLockScreen from './components/QuickUnlockLockScreen';
import { isQuickUnlockEnabled } from './utils/quickUnlock';
import StagingWatermark from './components/StagingWatermark';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const DEFAULT_INACTIVITY_TIMEOUT_MINUTES = 10;

const resolveInactivityTimeoutMs = () => {
  const configuredMinutes = Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES);
  const timeoutMinutes = Number.isFinite(configuredMinutes) && configuredMinutes > 0
    ? configuredMinutes
    : DEFAULT_INACTIVITY_TIMEOUT_MINUTES;
  return Math.floor(timeoutMinutes * 60 * 1000);
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  const [envMaintenanceMode] = useState(process.env.REACT_APP_MAINTENANCE_MODE === 'true');
  const [serverVersion, setServerVersion] = useState(undefined);
  const [serverMode, setServerMode] = useState(undefined);
  const [clientVersion] = useState(packageJson.version);
  const [clientMode, setClientMode] = useState(process.env.NODE_ENV || 'development');
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState(null);
  const [sessionRestoring, setSessionRestoring] = useState(() => sessionStorage.getItem('isLoggedIn') !== 'true');
  const [locked, setLocked] = useState(false);
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const inactivityTimeoutMs = resolveInactivityTimeoutMs();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || sessionStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    document.documentElement.style.colorScheme = savedTheme;
  }, []);

  // Restore an already-authenticated session on relaunch: the authToken
  // cookie can still be valid for up to 24h after the tab/PWA was closed,
  // but sessionStorage (and with it the isLoggedIn flag) is wiped on close.
  // Without this, a still-valid session was forcing a full re-login every
  // time the installed app reopened.
  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') === 'true') {
      setSessionRestoring(false);
      return;
    }

    let cancelled = false;

    restoreSession().then((res) => {
      if (cancelled || !res) return;

      document.documentElement.setAttribute('data-theme', res.user.theme || 'light');
      document.documentElement.style.colorScheme = res.user.theme || 'light';

      if (isQuickUnlockEnabled()) {
        setLocked(true);
      } else if (location.pathname === '/') {
        // The PWA always relaunches at '/', which unconditionally renders the
        // login page -- without this, a restored session sits on the login
        // screen despite already being authenticated. Route through
        // /subscription-check, same as a fresh login, rather than straight to
        // /dashboard, so subscription-lapsed accounts land on the right gate.
        navigate('/subscription-check', { replace: true });
      }
    }).catch(() => {
      // No valid session cookie -- fall through to the normal login screen.
    }).finally(() => {
      if (!cancelled) setSessionRestoring(false);
    });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (sessionRestoring) return;

    const publicPaths = new Set([
      '/',
      '/session-timeout',
      '/about',
      '/pricing',
      '/contact',
      '/terms',
      '/privacy',
      '/grievance-redressal',
      '/cancellation-refunds',
      '/confirm-active',
      '/subscription-check',
      '/subscribe',
      '/nominee-access-info',
      '/nominee',
    ]);

    const currentPath = location.pathname.toLowerCase();
    const isConfirmActiveTokenPath = currentPath.startsWith('/confirm-active/');
    // The objection link always carries a row id, so it is never the bare path and can
    // never match the Set above - it needs a prefix test, like /confirm-active/. Missing
    // this redirects the objector to the landing page and silently loses their objection,
    // which is exactly what happened to the real nominee-login link once before
    // (nominee-public-path-guard-fix.md): the guard, not the route, was the bug.
    const isNomineeObjectionPath = currentPath.startsWith('/nominee-objection/');

    if (!isLoggedIn && !publicPaths.has(currentPath) && !isConfirmActiveTokenPath && !isNomineeObjectionPath) {
      navigate('/');
    }
  }, [isLoggedIn, sessionRestoring, navigate, location.pathname]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let timeoutId;
    let isLoggingOut = false;

    const timeoutTrigger = async () => {
      // With quick unlock configured, go idle into the same local lock
      // screen used on relaunch instead of a hard logout -- the session
      // cookie stays valid, and resuming only needs the PIN/biometric.
      if (isQuickUnlockEnabled()) {
        setLocked(true);
        return;
      }

      if (isLoggingOut) {
        return;
      }

      isLoggingOut = true;

      try {
        await logout();
      } catch {
        sessionStorage.clear();
      } finally {
        navigate('/session-timeout', { replace: true });
      }
    };

    const resetInactivityTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(timeoutTrigger, inactivityTimeoutMs);
    };

    const onUserActivity = () => {
      resetInactivityTimer();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((eventName) => {
      window.addEventListener(eventName, onUserActivity, { passive: true });
    });

    const onVisibilityChange = () => {
      if (!document.hidden) {
        resetInactivityTimer();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    resetInactivityTimer();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      events.forEach((eventName) => {
        window.removeEventListener(eventName, onUserActivity);
      });

      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn, inactivityTimeoutMs, navigate]);

  useEffect(() => {
    document.title = "Eternal Locker™";
    fetchServerVersion().then(response => {
      setServerVersion(response.version);
      setServerMode(response.mode);
    }).catch(() => {
      setServerVersion(null);
    });
  }, []);

  // The stored choice, or the banner if there isn't one. Reading, parsing and validating
  // it all live in utils/cookieConsent.js now (C1.10) - the Privacy Choices panel writes
  // to the same store, so it cannot be a private detail of this component any more.
  useEffect(() => {
    const stored = readCookiePreferences();
    setCookiePreferences(stored);
    setShowCookieBanner(!stored);
  }, []);

  // ...and re-read when anything else writes it. Today that is the analytics toggle in
  // Privacy Choices: without this, turning analytics off there would update the server and
  // leave GA4 running in this tab until a reload, while the screen claimed otherwise.
  useEffect(() => subscribeToCookiePreferences((next) => {
    setCookiePreferences(next);
    setShowCookieBanner(false);
  }), []);

  // Reconcile this browser's choice with the account's record, once per signed-in
  // session (C1.10). The rule - most recent decision wins, whichever side made it - lives
  // in utils/analyticsConsentSync.js with the reasoning; the ref just stops it re-running
  // on every render, since isLoggedIn is read from sessionStorage rather than held as
  // state. Failures are swallowed there: analytics consent is not worth blocking a page
  // load over, and the next session tries again.
  const analyticsReconciledRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || analyticsReconciledRef.current) return;
    analyticsReconciledRef.current = true;
    reconcileAnalyticsConsent();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return;
    setAnalyticsConsent(GA_MEASUREMENT_ID, cookiePreferences);
  }, [cookiePreferences]);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !cookiePreferences?.analyticsUsage) return;
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    trackPageView(GA_MEASUREMENT_ID, pagePath, cookiePreferences);
  }, [location.pathname, location.search, location.hash, cookiePreferences]);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !cookiePreferences?.analyticsDevice) return;
    trackDeviceDiagnostics(GA_MEASUREMENT_ID, cookiePreferences);
  }, [cookiePreferences]);

  const handleCookieConsent = (preferences) => {
    const nextPreferences = saveCookiePreferences(preferences);
    setCookiePreferences(nextPreferences);
    setShowCookieBanner(false);

    // C1.10: the choice becomes evidence, not just browser state. Only for a signed-in
    // user - `user_consents` is keyed by account, and the banner is shown to signed-out
    // visitors too, whose choice stays local until they next sign in and reconcile.
    // Not awaited: analytics has already started or stopped, and the recording is a
    // consequence of that, not a precondition for it.
    if (isLoggedIn) {
      pushAnalyticsConsent(nextPreferences);
    }
  };

  const openCookiePreferences = () => {
    setShowCookieBanner(true);
  };

  useEffect(() => {
    const canonicalByPath = {
      '/privacy': 'https://nybbletechnologies.com/privacy.html',
      '/terms': 'https://nybbletechnologies.com/terms.html',
    };

    const canonicalHref = canonicalByPath[location.pathname.toLowerCase()];
    const existingCanonical = document.head.querySelector('link[rel="canonical"][data-managed="app"]');

    if (!canonicalHref) {
      if (existingCanonical) {
        existingCanonical.remove();
      }
      return;
    }

    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalHref);
      return;
    }

    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonicalHref);
    link.setAttribute('data-managed', 'app');
    document.head.appendChild(link);
  }, [location.pathname]);

  // Wait for serverVersion to be fetched before deciding maintenance mode
  if (!serverVersion) return null;
  const maintenanceMode = envMaintenanceMode || serverVersion === null || serverVersion !== clientVersion;
  if (maintenanceMode) {
    return (
      <>
        {isStaging && <StagingWatermark />}
        <MaintenanceInfo />
      </>
    );
  }

  if (serverMode !== clientMode) {
    return (
      <>
        {isStaging && <StagingWatermark />}
        <MaintenanceInfo />
      </>
    );
  }

  // Wait for the relaunch session-restore check above before deciding what to
  // render, so a still-valid session doesn't flash the login screen first.
  if (sessionRestoring) return null;

  if (locked) {
    return (
      <>
        {isStaging && <StagingWatermark />}
        <QuickUnlockLockScreen
          email={sessionStorage.getItem('email')}
          onUnlocked={() => {
            setLocked(false);
            if (location.pathname === '/') {
              navigate('/subscription-check', { replace: true });
            }
          }}
          onUsePassword={async () => {
            try {
              await logout();
            } catch {
              sessionStorage.clear();
            }
            setLocked(false);
            navigate('/', { replace: true });
          }}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark flex flex-col p-4 font-sans antialiased">
      <style>{`
        /* Load Inter font */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
        body { font-family: 'Inter', sans-serif; }
      `}</style>
      {isStaging && <StagingWatermark />}
      {process.env.NODE_ENV === 'development' && (
        <>
        <hr className="fixed top-4 left-0 right-0 border-red-500"/>
        <hr className="fixed bottom-4 left-0 right-0 border-red-500"/>
        <div className="fixed h-full w-0 border-l border-red-500 left-4 top-0 bottom-0"></div>
        <div className="fixed h-full w-0 border-r border-red-500 right-4 top-0 bottom-0"></div>

        <div className="fixed bottom-5 right-5 text-gray-500 text-xs bg-red-900 p-2 text-white">
          <div>Client Mode: {clientMode}</div>
          <div>Client Version: {clientVersion}</div>
          <div>Server Mode: {serverMode}</div>
          {serverVersion && <div>Server Version: {serverVersion}</div>}
          <div>Inactivity Timeout: {Math.floor(inactivityTimeoutMs / 1000 / 60)} minutes</div>
        </div>
        </>
      )}
      <div className="w-full flex-1">
        <Routes>
          <Route path="/" element={<AuthPage />} />
          <Route path="/session-timeout" element={<SessionTimeoutPage />} />
          <Route path="/subscription-check" element={<SubscriptionCheckPage />} />
          <Route path="/confirm-active" element={<ConfirmActivePage />} />
          <Route path="/confirm-active/:token" element={<ConfirmActivePage />} />
          {/* Reached from a link in the nominee designation notice, by someone with no
              account. /nominee-objection, NOT under /nominee/ - nginx proxies that prefix
              to Express, so a client route there would be answered with JSON. */}
          <Route path="/nominee-objection/:id" element={<NomineeObjectionPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/subscribe" element={<SubscriptionPage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/manage-nominees" element={<NomineesPage />} />
          <Route path="/admin-users" element={<AdminUsersPage />} />
          <Route path="/admin-plans" element={<AdminPlansPage />} />
          <Route path="/admin-incidents" element={<AdminIncidentsPage />} />
          <Route path="/admin-audit" element={<AdminAuditLogsPage />} />
          <Route path="/admin-encryption" element={<AdminEncryptionPage />} />
          <Route path="/admin-dashboard" element={<AdminDashboardPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/grievance-redressal" element={<GrievanceRedressalPage />} />
          <Route path="/cancellation-refunds" element={<CancellationRefundsPage />} />
          <Route path="/nominee-access-info" element={<NomineeAccessInfoPage />} />
          <Route path="*" element={<AuthPage />} />
        </Routes>
      </div>

      <footer className="w-full text-center py-6 mt-8 text-gray-400 text-xs border-t border-gray-800">
        <div className="mb-2">
          &copy; 2026 Eternal Locker™. A product of <a href="https://www.nybbletechnologies.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 font-semibold hover:underline">Nybble Technologies Private Limited</a>.
          All rights reserved. <span>{clientVersion}</span>
        </div>

        <div className="flex flex-col sm:flex-row justify-center mt-2">
          <a href="/dashboard" className="px-3 py-1 hover:text-accent-main transition-colors">Home</a>
          <a href="/about" className="px-3 py-1 hover:text-accent-main transition-colors">About Us</a>
          <a href="/pricing" className="px-3 py-1 hover:text-accent-main transition-colors">Pricing</a>
          <a href="/contact" className="px-3 py-1 hover:text-accent-main transition-colors">Contact Us</a>
          <a href="/privacy" className="px-3 py-1 hover:text-accent-main transition-colors">Privacy Policy</a>
          <a href="/terms" className="px-3 py-1 hover:text-accent-main transition-colors">Terms &amp; Conditions</a>
          <a href="/grievance-redressal" className="px-3 py-1 hover:text-accent-main transition-colors">Grievance Redressal</a>
          <a href="/cancellation-refunds" className="px-3 py-1 hover:text-accent-main transition-colors">Cancellation &amp; Refunds</a>
          <a href="/nominee-access-info" className="px-3 py-1 hover:text-accent-main transition-colors">Nominee Access</a>
          <button type="button" onClick={openCookiePreferences} className="px-3 py-1 hover:text-accent-main transition-colors">
            Cookie Preferences
          </button>
        </div>
      </footer>

      {showCookieBanner && (
        <CookieConsentBanner initialPreferences={cookiePreferences} onSave={handleCookieConsent} />
      )}
    </div>
  );
}

